import { usePaginationCDLMap } from '@centric-os/helpers';
import type {
  CDLQueryParams,
  Menu,
  MenuSizeResponse,
  MenuWithTypeAndCatCount,
  RecordEntityId,
  RecordList,
  V2Menu,
  Category,
  MenuPublishError,
} from '@centric-os/types';
import { MenuType } from '@centric-os/types';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { defineStore, type StateTree } from 'pinia';
import { isEmpty } from 'lodash-es';
import useBrandRefresh from '../composables/use-brand-refresh';

export type { MenuWithTypeAndCatCount } from '@centric-os/types';

interface State extends StateTree {
  menu: MenuWithTypeAndCatCount;
  v2Menu: V2Menu;
  menus: MenuWithTypeAndCatCount[];
  menuPublishErrors: MenuPublishError[];
}

export function useMenuStore(storeId = 'menu') {
  const store = defineStore(storeId, {
    state: (): State => ({
      menu: null,
      v2Menu: null,
      menus: [],
      menuPublishErrors: [],
    }),
    actions: {
      async getMenusWithCategoryCount(
        brandId: RecordEntityId,
        noPagination = false,
        config: AxiosRequestConfig = {},
        categoriesNeededWithMenuData = false,
        categoriesFilter: { limit: number; page: number } | {} = { limit: 1, page: 1 },
      ): Promise<void> {
        const { mapMenuQueryParams, mapCentricOSPaginationMeta } = usePaginationCDLMap();

        const params: CDLQueryParams = {
          sort_by: 'created_at',
          sort_order: 'DESC',
        };

        const response = await this.cdlApi.get<RecordList<Menu>>(
          `/menu/v3/draft/brand/${brandId}/menus`,
          {
            ...config,
            signal: this.abortControllers.get('getMenusWithCategoryCount')?.signal,
            params: noPagination ? params : mapMenuQueryParams(this.pagination.queryParams),
          },
        );

        const { results, meta } = response.data;
        this.menus = results as Menu[];

        const categoryFetchPromises = results.map((menuResult, index) =>
          this.fetchCategoryCount(menuResult.id as string, categoriesFilter, index),
        );

        if (categoriesNeededWithMenuData) {
          await Promise.all(categoryFetchPromises);
        } else {
          categoryFetchPromises.forEach((promise) =>
            promise.catch((error: any) => console.error('Error fetching category count:', error)),
          );
        }

        if (!noPagination) {
          this.updatePaginationWithMeta(mapCentricOSPaginationMeta(meta));
        }
      },
      async fetchCategoryCount(menuId: string, params: any, index: number) {
        const { data } = await this.cdlApi.get(`/menu/v3/draft/menu/${menuId}/categories`, {
          params,
        });
        this.menus[index].totalCategories = data.meta.total_results;
        this.menus[index].categories = data.results;
      },
      async getAllMenus(noPagination = false, config: AxiosRequestConfig = {}) {
        const { mapMenuQueryParams, mapCentricOSPaginationMeta } = usePaginationCDLMap();

        const defaultParams: CDLQueryParams = {
          sort_by: 'created_at',
          sort_order: 'DESC',
          relationships: ['brand'],
        };

        const params: CDLQueryParams = noPagination
          ? defaultParams
          : {
              ...mapMenuQueryParams(this.pagination.queryParams),
              relationships: defaultParams.relationships,
            };

        const response = await this.cdlApi.get<RecordList<Menu>>(`/menu/v3/draft/menus`, {
          ...config,
          signal: this.abortControllers.get('getAllMenus')?.signal,
          params: {
            ...config?.params,
            ...params,
          },
        });

        const { results, meta } = response.data;
        this.menus = results as Menu[];
        if (!noPagination) {
          this.updatePaginationWithMeta(mapCentricOSPaginationMeta(meta));
        }
        return this.menus;
      },
      async publishMenu(menuId: RecordEntityId) {
        await this.cdlApi.post<any, AxiosResponse<any, any>, Partial<any>>(
          `/menu/v3/draft/menu/${menuId}/publish`,
        );
      },

      /**
       * Fetches a menu by its ID and updates the store's menu state.
       *
       * @async
       * @param {RecordEntityId} id - The unique identifier of the menu to be fetched.
       * @returns {Promise<void>} - A promise that resolves when the menu has been successfully fetched and the store has been updated.
       * @throws {Error} - Throws an error if the API call fails.
       *
       * @example
       * // Example usage:
       * await store.getMenu('menu-id-123');
       */
      async getMenu(id: RecordEntityId, config: AxiosRequestConfig = {}): Promise<void> {
        const response = await this.cdlApi.get<Menu>(`/menu/v3/draft/menu/${id}`, {
          params: { relationships: ['brand'] },
          ...config,
        });
        // Check later if we create a state only for published ones
        this.menu = response.data;
      },

      async getPublishedMenu(id: RecordEntityId): Promise<void> {
        const response = await this.cdlApi.get<Menu>(`/menu/v3/menu/${id}`, {
          params: { relationships: ['brand'] },
        });
        this.menu = response.data;
      },
      async getMenuCategories(id: RecordEntityId) {
        const response = await this.cdlApi.get<RecordList<Category>>(
          `/menu/v3/draft/menu/${id}/categories`,
          {
            signal: this.abortControllers.get('getMenuCategories')?.signal,
          },
        );
        const { results } = response.data;
        this.menu.categories = results;
      },
      async getV4Menu(id: RecordEntityId, params?: CDLQueryParams): Promise<Menu> {
        const response = await this.cdlApi.get<Menu>(`/menu/v4/menu/${id}`, { params });
        this.menu = response.data;
        this.menu.totalCategories = response.data.categories?.length;

        if (!isEmpty(this.menus)) {
          const menuIndex = this.menus.findIndex((menu) => menu.id === id);
          if (menuIndex !== -1) {
            this.menus[menuIndex] = this.menu;
          }
        }
        return response.data;
      },
      async createV4Menu(brand_id: RecordEntityId, payload: Partial<Menu>): Promise<Menu> {
        const response = await this.cdlApi.post<Menu, AxiosResponse<Menu, any>, Partial<Menu>>(
          `/menu/v4/brand/${brand_id}/menu`,
          payload,
        );

        this.menu = response.data;
        return response.data;
      },

      async createMenu(payload: Partial<Menu>): Promise<Menu> {
        const { refreshPublishedBrandAfterUpdates } = useBrandRefresh();

        const response = await this.cdlApi.post<Menu, AxiosResponse<Menu, any>, Partial<Menu>>(
          `/menu/v3/draft/menu`,
          payload,
        );

        this.menu = response.data;

        refreshPublishedBrandAfterUpdates();
        return response.data;
      },

      async updateMenu(payload: Partial<Menu>, skipStoreRefresh: boolean = false): Promise<Menu> {
        const { refreshPublishedBrandAfterUpdates } = useBrandRefresh();

        if (payload && payload.id) {
          const response = await this.cdlApi.patch<Menu, AxiosResponse<Menu, any>, Partial<Menu>>(
            `/menu/v3/draft/menu/${payload.id}`,
            {
              ...payload,
              version: undefined,
            },
          );

          if (!skipStoreRefresh) {
            this.menu = response.data;
          }
        } else {
          return Promise.reject(new Error('no menu selected'));
        }

        refreshPublishedBrandAfterUpdates();
      },
      async updateV4Menu(brand_id: RecordEntityId, payload: Partial<Menu>): Promise<Menu> {
        if (payload?.id) {
          const response = await this.cdlApi.patch<Menu, AxiosResponse<Menu>, Partial<Menu>>(
            `/menu/v4/brand/${brand_id}/menu/${payload.id}`,
            {
              ...payload,
              version: undefined,
            },
          );

          this.menu = response.data;
          const { refreshPublishedBrandAfterUpdates } = useBrandRefresh();

          refreshPublishedBrandAfterUpdates();
        } else {
          return Promise.reject(new Error('no menu selected'));
        }
      },
      async reorderV4Menus(
        brand_id: RecordEntityId,
        menus: Pick<Menu, 'id' | 'sequence'>[],
      ): Promise<void> {
        await this.cdlApi.patch<
          void,
          AxiosResponse<void>,
          { menus: Pick<Menu, 'id' | 'sequence'>[] }
        >(`/menu/v4/brand/${brand_id}/menus/reorder`, { menus });
        const { refreshPublishedBrandAfterUpdates } = useBrandRefresh();
        refreshPublishedBrandAfterUpdates();
      },
      async uploadMenuThumbnailV4(
        brand_id: RecordEntityId,
        id: RecordEntityId,
        data: string | ArrayBuffer,
      ): Promise<void> {
        await this.cdlApi.post<any, AxiosResponse<any, any>, Partial<any>>(
          `/menu/v4/menu/${id}/attachment/thumbnail`,
          {
            brand_id,
            data,
          },
        );
      },

      async uploadMenuThumbnailV3(id: RecordEntityId, data: string | ArrayBuffer): Promise<void> {
        await this.cdlApi.post<any, AxiosResponse<any, any>, Partial<any>>(
          `/menu/v3/draft/menu/${id}/attachment/thumbnail`,
          {
            data,
          },
        );
      },
      async deleteV3Menu(id: RecordEntityId): Promise<void> {
        const { refreshPublishedBrandAfterUpdates } = useBrandRefresh();

        await this.cdlApi.delete<Menu, AxiosResponse<Menu, any>, Menu>(`/menu/v3/draft/menu/${id}`);

        refreshPublishedBrandAfterUpdates();
      },
      async deleteV4Menu(brand_id: RecordEntityId, id: RecordEntityId): Promise<void> {
        const { refreshPublishedBrandAfterUpdates } = useBrandRefresh();
        await this.cdlApi.delete<Menu, AxiosResponse<Menu>, Menu>(
          `/menu/v4/brand/${brand_id}/menu/${id}`,
        );
        refreshPublishedBrandAfterUpdates();
      },
      async duplicateV3Menu(menuId: RecordEntityId, payload: Partial<Menu>): Promise<Menu> {
        const response = await this.cdlApi.post<Partial<Menu>, AxiosResponse<Menu>>(
          `/menu/v3/draft/menu/${menuId}/duplicate`,
          payload,
        );

        return response.data;
      },
      async duplicateV4Menu(
        brand_id: RecordEntityId,
        menuId: RecordEntityId,
        payload: Partial<Menu>,
      ): Promise<Menu> {
        const response = await this.cdlApi.post<Partial<Menu>, AxiosResponse<Menu>>(
          `/menu/v4/brand/${brand_id}/menu/${menuId}/duplicate`,
          payload,
        );

        return response.data;
      },
      async verifyMenuBarcodes(menuIds: RecordEntityId[]): Promise<void> {
        const params = { menu_ids: menuIds };
        const response = await this.cdlApi.get<Record<'warnings', MenuPublishError[]>>(
          `/menu/v3/draft/menus/verify-publish`,
          {
            params: params,
          },
        );

        this.menuPublishErrors = response.data?.warnings.filter((warning) => {
          return !(warning.type === 'duplicate_barcode' && !warning.barcode);
        });
      },
      async getPublishedMenus(brandId: RecordEntityId) {
        if (!brandId) return;
        const response = await this.cdlApi.get(`menu/v3/brand/${brandId}/menus?nocache=true`);
        if (!isEmpty(response.data.results)) {
          response.data.results.map((menu: MenuWithTypeAndCatCount) => {
            menu.meta = {
              menu_type: MenuType.CENTRIC_OS,
            };
          });
        }
        const { data } = response;
        this.menus = data.results;
      },
      async getCompanyMenus(companyId: RecordEntityId): Promise<V2Menu[] | undefined> {
        if (!companyId) return;
        const response = await this.cdlApi.get(`/menu/company/${companyId}`);

        return response.data.menus;
      },
      async getStationMenusByCompany(stationId: RecordEntityId, companyId: RecordEntityId) {
        const companyMenus = await this.getCompanyMenus(companyId);
        if (!companyMenus) return;
        this.menus = companyMenus
          .filter(
            (menu) => menu.location_brand === stationId && !menu.is?.disabled && menu.is?.linked,
          )
          .map((menu) => ({
            id: menu.id,
            name: menu.label.en,
            meta: { menu_type: MenuType.V2 },
          }));
      },
      async getV2Menu(id: RecordEntityId, params?: any): Promise<void> {
        const response = await this.cdlApi.get<V2Menu>(`/menu/${id}`, {
          params,
        });
        this.v2Menu = response.data;
      },
      async patchV2Menu(id: RecordEntityId, payload: Partial<V2Menu>) {
        const response = await this.cdlApi.patch<V2Menu>(`/menu/${id}`, payload);
        return response.data;
      },
      async scheduleMenu(payload: {
        menu_id: RecordEntityId;
        station_id: RecordEntityId;
        price_level_id: RecordEntityId;
      }): Promise<Menu> {
        const response = await this.cdlApi.post('/menu/v4/schedule-menu', payload);
        return response.data;
      },
      async unscheduleMenu(payload: {
        menu_id: RecordEntityId;
        station_id: RecordEntityId;
      }): Promise<void> {
        await this.cdlApi.post(`/menu/v4/unschedule-menu`, payload);
      },
      async getMenuSize(id: RecordEntityId): Promise<number> {
        const response = await this.cdlApi.get<MenuSizeResponse>(`/menu/v4/menu/${id}/size`);
        // Use compressed size (theoretical size) for the limit check
        return response.data.size.compressed;
      },
    },
    pagination: false,
  });
  return store();
}
